/**
 * Contraseña nueva con el enlace del correo — `OD-0027 D-05`, `OD-0018 E-06`.
 *
 * Al cambiarla, la API cierra TODAS las sesiones del usuario y borra la cookie
 * de quien la envía. Por eso, al terminar, se vuelve a la pantalla de entrada
 * y no a una sesión.
 */
import { useState } from "react";
import { api, ESTADO_VALIDACION } from "../api/cliente";
import { t } from "../i18n/es";
import { contrasenaCorta, MINIMO_CONTRASENA } from "../ui/contrasena";
import { Aviso, Boton, Campo, Tarjeta } from "../ui/primitivas";

export function PantallaNuevaContrasena({
  token,
  hayConexion,
  onCambiada,
  onPedirOtro,
  onVolver,
}: {
  token: string | null;
  hayConexion: boolean;
  onCambiada: () => void;
  onPedirOtro: () => void;
  onVolver: () => void;
}) {
  const [password, setPassword] = useState("");
  const [errorPassword, setErrorPassword] = useState<string | null>(null);
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enlaceMuerto, setEnlaceMuerto] = useState(token === null);

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (token === null) return;
    setErrorGeneral(null);
    if (password === "") {
      setErrorPassword(t.contrasenaNueva.faltaPassword);
      return;
    }
    // Hallazgo 12: lo que la API rechazaría, antes de gastar el enlace en ello.
    if (contrasenaCorta(password)) {
      setErrorPassword(t.contrasenaNueva.corta(MINIMO_CONTRASENA));
      return;
    }
    setErrorPassword(null);

    setEnviando(true);
    const r = await api.completarRecuperacion(token, password);
    setEnviando(false);

    if (r.ok) {
      setPassword("");
      onCambiada();
      return;
    }
    if (r.estado === 0) setErrorGeneral(t.comun.sinRed);
    else if (r.estado === 429) setErrorGeneral(t.comun.demasiadosIntentos);
    // 422: o la contraseña no cumple la política (el mensaje de la API trae el
    // mínimo vigente, OD-0027 D-01), o el enlace no vale.
    else if (r.estado === ESTADO_VALIDACION && r.error !== null && r.error.message.includes("contraseña")) {
      setErrorPassword(r.error.message);
    } else if (r.estado === ESTADO_VALIDACION) setEnlaceMuerto(true);
    else setErrorGeneral(t.comun.errorGenerico);
  }

  if (enlaceMuerto) {
    return (
      <Tarjeta titulo={t.nuevaContrasena.titulo}>
        <Aviso tono="error">{t.enlace.noValido}</Aviso>
        <div className="acciones">
          <Boton onClick={onPedirOtro}>{t.enlace.pedirOtraRecuperacion}</Boton>
          <Boton variante="secundario" onClick={onVolver}>
            {t.recuperar.volver}
          </Boton>
        </div>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta titulo={t.nuevaContrasena.titulo}>
      <p>{t.nuevaContrasena.detalle}</p>
      <form onSubmit={enviar} noValidate>
        <Campo
          id="nueva-password"
          etiqueta={t.nuevaContrasena.password}
          valor={password}
          onChange={(v) => {
            setPassword(v);
            setErrorPassword(null);
          }}
          tipo="password"
          mostrar={t.verContrasena}
          autoComplete="new-password"
          ayuda={t.contrasenaNueva.ayuda}
          error={errorPassword}
          autoFocus
        />
        {errorGeneral !== null ? <Aviso tono="error">{errorGeneral}</Aviso> : null}
        {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
        <Boton tipo="submit" deshabilitado={enviando || !hayConexion}>
          {enviando ? t.nuevaContrasena.guardando : t.nuevaContrasena.guardar}
        </Boton>
      </form>
    </Tarjeta>
  );
}
